import { toMs } from './timeFormat';
import type { EventMetadata, LiveSessionEvent } from './types';

type StudentNavCause = 'sync' | 'user';

export interface SlideSyncPoint {
  student: number | null;
  // Set only on the student's own nav points; interpolated and teacher points carry null.
  studentCause: StudentNavCause | null;
  // Seconds since the session started.
  t: number;
  teacher: number | null;
}

interface SlideSyncSeries {
  maxSlide: number;
  points: SlideSyncPoint[];
  // False when no event reported a position, so slides are numbered by id order instead.
  usesPositions: boolean;
}

interface SeriesOptions {
  end: number;
  start: number;
  studentId: number | null;
}

const slideKey = (m: EventMetadata) => `${m.slide_type ?? 'Slide'}:${m.slide_id}`;

const isTeacherNav = (e: LiveSessionEvent) => (
  e.event_type === 'navigate_to_slide' && e.metadata.slide_id != null && !e.metadata.rejected_reason
);

const isStudentNav = (e: LiveSessionEvent, studentId: number) => (
  e.event_type === 'nav_applied' && e.metadata.slide_id != null
    && e.actor?.type === 'Student' && e.actor.id === studentId
);

// Server nav rows only carry ids; client rows (nav_applied, teacher_nav_sent, and navigate_to_slide
// once the teacher client sends position) map each id to its 1-based place in the deck.
const positionLookup = (events: LiveSessionEvent[]): Map<string, number> => {
  const lookup = new Map<string, number>();
  events.forEach((e) => {
    const { position, slide_id: slideId } = e.metadata;
    if (slideId != null && typeof position === 'number' && Number.isFinite(position)) {
      lookup.set(slideKey(e.metadata), position);
    }
  });
  return lookup;
};

// Slides in one deck are usually created together, so id order is a usable stand-in for deck order.
// Ranked over every nav in the session so the numbering does not shift with the selected student.
const rankLookup = (events: LiveSessionEvent[]): Map<string, number> => {
  const ids = new Map<string, number>();
  events
    .filter(e => isTeacherNav(e) || (e.event_type === 'nav_applied' && e.metadata.slide_id != null))
    .forEach(e => ids.set(slideKey(e.metadata), Number(e.metadata.slide_id)));
  const sorted = [...ids.entries()].sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));
  return new Map(sorted.map(([key], index) => [key, index + 1]));
};

export const buildSlideSyncSeries = (
  events: LiveSessionEvent[],
  { end, start, studentId }: SeriesOptions,
): SlideSyncSeries => {
  const teacherNavs = events.filter(isTeacherNav);
  const studentNavs = studentId === null ? [] : events.filter(e => isStudentNav(e, studentId));
  const positions = positionLookup(events);
  const usesPositions = positions.size > 0;
  const numbering = usesPositions ? positions : rankLookup(events);

  const navs = [
    ...teacherNavs.map(e => ({ event: e, who: 'teacher' as const })),
    ...studentNavs.map(e => ({ event: e, who: 'student' as const })),
  ].sort((a, b) => toMs(a.event.occurred_at) - toMs(b.event.occurred_at) || a.event.id - b.event.id);

  const points: SlideSyncPoint[] = [];
  let teacher: number | null = null;
  let student: number | null = null;
  let maxSlide = 0;

  navs.forEach(({ event, who }) => {
    const slide = numbering.get(slideKey(event.metadata));
    if (slide === undefined) return;
    maxSlide = Math.max(maxSlide, slide);
    let studentCause: StudentNavCause | null = null;
    if (who === 'teacher') {
      teacher = slide;
    } else {
      student = slide;
      studentCause = event.metadata.cause === 'user' ? 'user' : 'sync';
    }
    points.push({ student, studentCause, t: Math.round((toMs(event.occurred_at) - start) / 1000), teacher });
  });

  // Carry both lines to the end of the window so "where is each of them now" is readable.
  const last = points[points.length - 1];
  const endT = Math.round((end - start) / 1000);
  if (last && endT > last.t) points.push({ student, studentCause: null, t: endT, teacher });

  return { maxSlide, points, usesPositions };
};
